import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Receipt,
  Globe2,
  BookOpenCheck,
  Plug,
  Settings as SettingsIcon,
  LogOut,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCompany } from "@/lib/company";
import { SelectNative } from "@/components/ui/select-native";
import { Button } from "@/components/ui/button";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/overview", label: "Overview", icon: LayoutDashboard },
  { to: "/catalogue", label: "Catalogue", icon: Package },
  { to: "/orders", label: "Orders", icon: ShoppingCart },
  { to: "/tax", label: "Tax & VAT", icon: Receipt },
  { to: "/markets", label: "Markets", icon: Globe2 },
  { to: "/books", label: "Books", icon: BookOpenCheck },
  { to: "/integrations", label: "Integrations", icon: Plug },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const { companies, activeId, setActiveId, previewMode, exitPreview } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = user
    ? `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() || "U"
    : "U";

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r bg-card md:flex">
        <div className="flex h-16 items-center gap-2 border-b px-6">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">
            G
          </span>
          <span className="font-display text-xl font-semibold tracking-tight">GoEcom</span>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-3 text-xs text-muted-foreground">
          Import / export OS for UK sellers
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between gap-4 border-b bg-card px-4 md:px-6">
          <div className="flex items-center gap-3">
            {previewMode ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                <Sparkles className="h-3.5 w-3.5" /> Preview, no sign-up
              </span>
            ) : (
              companies.length > 0 && (
                <SelectNative
                  value={activeId ?? ""}
                  onChange={(e) => setActiveId(Number(e.target.value))}
                  className="h-9 max-w-[16rem]"
                  aria-label="Active company"
                >
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </SelectNative>
              )
            )}
          </div>
          <div className="flex items-center gap-3">
            {previewMode ? (
              <Button
                onClick={() => {
                  exitPreview();
                  navigate("/signup");
                }}
              >
                Claim this dashboard
              </Button>
            ) : (
              <>
                <div className="hidden text-right sm:block">
                  <div className="text-sm font-medium leading-tight">
                    {user?.first_name} {user?.last_name}
                  </div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </div>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold">
                  {initials}
                </span>
                <button
                  onClick={logout}
                  className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  title="Log out"
                  aria-label="Log out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-background p-4 md:p-8">
          <ErrorBoundary resetKey={location.pathname + (activeId ?? "")}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
